import React from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';

import { FeatureFlagsManager } from '../../utils/core/feature-flags-manager';
import { CompanyAdmins } from '../components/companies/company/company-admins';
import { CompanyLicenses } from '../components/companies/company/company-licenses';
import { CompanyOverview } from '../components/companies/company/company-overview';
import { CompanyList } from '../components/companies/company-list';
import { PendingProjectTransfers } from '../components/companies/pending-project-transfers/pending-project-transfers';
import { DroneLoader } from '../components/drone-loader/drone-loader';
import { OverlayLoader } from '../components/overlay-loader/overlay-loader';
import { PageContainer } from '../components/page-container';
import type { GetCompanyResponse } from '../state/company-state';
import { CompanyStateProvider, useCompanyState } from '../state/company-state';
import { useToaster } from '../state/toaster-state';

type CompaniesOutletContext = {
  selectedCompany?: GetCompanyResponse;
  setSelectedCompany: React.Dispatch<React.SetStateAction<GetCompanyResponse | undefined>>;
};

const useCompaniesLayoutContext = () => useOutletContext<CompaniesOutletContext>();

const CompaniesLayout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [selectedCompany, setSelectedCompany] = React.useState<GetCompanyResponse | undefined>();

  const companyPageTitles = React.useMemo(
    () => ({
      '': t('menu.overview.title', { ns: 'navigation' }),
      licenses: t('menu.licenses.title', { ns: 'navigation' }),
      admins: t('menu.companyAdmins.title', { ns: 'navigation' }),
      'pending-transfers': t('menu.pendingProjectTransfers.title', { ns: 'navigation' }),
    }),
    [t],
  );

  const pageTitle = React.useMemo((): string => {
    if (!selectedCompany) {
      return t('menu.companies.title', { ns: 'navigation' });
    }
    const match = location.pathname.match(/^\/companies\/[^/]+\/(.*)$/);
    const segment = (match?.[1] ?? '').replace(/\/.*$/, '');
    const page =
      companyPageTitles[segment as keyof typeof companyPageTitles] ?? companyPageTitles[''];
    return [page, selectedCompany.name].join(' - ');
  }, [location.pathname, selectedCompany, companyPageTitles, t]);

  const contextValue = React.useMemo(
    () => ({ selectedCompany, setSelectedCompany }),
    [selectedCompany],
  );

  return (
    <PageContainer title={pageTitle}>
      <Outlet context={contextValue} />
    </PageContainer>
  );
};

const CompaniesListRoute = () => {
  const { setSelectedCompany } = useCompaniesLayoutContext();

  React.useEffect(() => {
    setSelectedCompany(undefined);
  }, [setSelectedCompany]);

  return <CompanyList />;
};

/**
 * Renders inside CompanyStateProvider. Fetches company and syncs with layout context.
 * Company state is only in the tree when viewing a specific company (not on the list).
 */
const CompanyDetailLayoutContent = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToaster();
  const { t } = useTranslation();
  const { selectedCompany, setSelectedCompany } = useCompaniesLayoutContext();
  const { company, status: companyStatus, fetchCompany, reset } = useCompanyState();
  const [status, setStatus] = React.useState<'loading' | 'ready'>(
    selectedCompany?.id === companyId ? 'ready' : 'loading',
  );

  React.useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!companyId) {
        void navigate('/companies', { replace: true });
        setSelectedCompany(undefined);
        return;
      }

      if (selectedCompany?.id === companyId) {
        setStatus('ready');
        return;
      }

      setStatus('loading');
      setSelectedCompany((prev) => (prev?.id === companyId ? prev : undefined));

      try {
        const fetchedCompany = await fetchCompany(companyId);
        if (mounted) {
          setSelectedCompany(fetchedCompany);
          setStatus('ready');
        }
      } catch {
        if (mounted) {
          showToast({
            type: 'error',
            title: t('toaster.title.error', { ns: 'common' }),
            message: t('overview.loadError', { ns: 'companies' }),
          });
          setSelectedCompany(undefined);
          void navigate('/companies', { replace: true });
        }
      }
    };

    void load();

    return () => {
      mounted = false;
    };
  }, [companyId, navigate, selectedCompany?.id, setSelectedCompany, fetchCompany, showToast, t]);

  React.useEffect(() => {
    if (companyStatus === 'ready' && company?.id === companyId) {
      setSelectedCompany(company);
    }
  }, [company, companyId, companyStatus, setSelectedCompany]);

  React.useEffect(
    () => () => {
      setSelectedCompany(undefined);
      reset();
    },
    [setSelectedCompany, reset],
  );

  const isReady = status === 'ready';

  if (!isReady) {
    return FeatureFlagsManager.instance.isEnabled('SK-6974-drone-loader') ? (
      <DroneLoader mode="page-content" />
    ) : (
      <OverlayLoader visible={true}>
        <div style={{ minHeight: '320px' }} />
      </OverlayLoader>
    );
  }

  return <Outlet />;
};

/**
 * Company-state scope for company-specific routes. Only mounts when viewing a company detail.
 * When on the companies list, no CompanyStateProvider is in the tree.
 */
const CompanyDetailLayout = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!companyId) {
      void navigate('/companies', { replace: true });
    }
  }, [companyId, navigate]);

  if (!companyId) {
    return null;
  }

  return (
    <CompanyStateProvider>
      <CompanyDetailLayoutContent />
    </CompanyStateProvider>
  );
};

const CompanyOverviewRoute = () => {
  return <CompanyOverview showTitle={false} />;
};

const CompanyLicensesRoute = () => {
  return <CompanyLicenses showTitle={false} />;
};

const CompanyAdminsRoute = () => {
  return <CompanyAdmins showTitle={false} />;
};

const PendingProjectTransfersRoute = () => {
  return <PendingProjectTransfers showTitle={false} />;
};

export {
  CompaniesLayout,
  CompaniesListRoute,
  CompanyAdminsRoute,
  CompanyDetailLayout,
  CompanyLicensesRoute,
  CompanyOverviewRoute,
  PendingProjectTransfersRoute,
};
